import { FC } from 'react';
import { useTranslation } from '@packages/utils';
import ButtonGroup from 'components/Base/ButtonGroup/ButtonGroup';
import { Flex } from 'antd';
import { GlobeOutline } from 'assets/icon/icon';
import { Text } from 'components/Base/Typography';
import { useEditEventLanguageMutation } from 'redux/apiSlice/events.slice';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { EWorkshopLanguage, TRtkErrorType } from 'data/types/generic.types';

interface LanguageSelectorProps {
  eventId: string;
  language?: string;
  eventStatus?: string;
}

const LanguageSelector: FC<LanguageSelectorProps> = ({
  eventId,
  language,
  eventStatus,
}) => {
  const { t } = useTranslation();
  const { showToastNotification } = useShowToastNotification();
  const [editEventLanguage, { isLoading }] = useEditEventLanguageMutation();

  const isDisabled =
    eventStatus === 'PUBLISHED' || eventStatus === 'CANCELLED' || isLoading;

  const languageOptions = [
    {
      label: t('ENGLISH', 'English'),
      value: 'ENGLISH',
    },
    {
      label: t('SPANISH', 'Spanish'),
      value: 'SPANISH',
    },
  ];

  const handleLanguageChange = async (value: string) => {
    try {
      await editEventLanguage({
        eventId,
        language: value as EWorkshopLanguage,
      }).unwrap();

      showToastNotification({
        message: t('Language has been changed successfully.'),
        type: ENotificationType.SUCCESS,
      });
    } catch (error) {
      showToastNotification({
        message:
          (error as TRtkErrorType)?.data?.message ||
          'Failed to update language',
        type: ENotificationType.ERROR,
      });
    }
  };

  return (
    <Flex align="center" data-cy="event-language-selector" gap={8}>
      <Flex align="center" gap={8} style={{ width: 160 }}>
        <GlobeOutline size={24} />
        <Text color="#808080">Language</Text>
      </Flex>
      <ButtonGroup
        options={languageOptions}
        value={language || 'ENGLISH'}
        size="small"
        disabled={isDisabled}
        onChange={handleLanguageChange}
        style={{ width: 170 }}
      />
    </Flex>
  );
};

export default LanguageSelector;
